import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './RailTooltip.css';

/**
 * RailTooltip — a fast, reliable hover/focus tooltip for the collapsed nav rail
 * (Item 3). Native `title=` tooltips are slow (~1s) and easy to miss; this shows
 * after ~150ms, renders to a body portal (so the sidebar's overflow:hidden can't
 * clip it), and positions itself to the RIGHT of the trigger, vertically centered.
 *
 * Role-token themed, so it reads in dark / light / paperwhite. Keyboard users get
 * it too: React's onFocus/onBlur bubble from the wrapped control.
 */
const SHOW_DELAY_MS = 150;

export default function RailTooltip({ label, children }) {
  const [pos, setPos] = useState(null);
  const timer = useRef(null);
  const anchorRef = useRef(null);

  const show = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.top + r.height / 2, left: r.right + 8 });
    }, SHOW_DELAY_MS);
  }, []);

  const hide = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setPos(null);
  }, []);

  return (
    <span
      ref={anchorRef}
      className="rail-tooltip__anchor"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {pos && createPortal(
        <span className="rail-tooltip" role="tooltip" style={{ top: pos.top, left: pos.left }}>
          {label}
        </span>,
        document.body
      )}
    </span>
  );
}
